import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Inbox, Loader2, X, ExternalLink, Archive, Star, ArrowRight, Mail, Tag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "storylo_admin_token";

const STATUS_STYLE = {
  new:       { label: "New",       cls: "text-amber-300 bg-amber-500/15 border-amber-500/30" },
  reviewing: { label: "Reviewing", cls: "text-sky-300 bg-sky-500/15 border-sky-500/30" },
  featured:  { label: "Featured",  cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30" },
  archived:  { label: "Archived",  cls: "text-zinc-400 bg-zinc-500/15 border-zinc-500/30" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

/* ──────────────────────────────────────────────────────────────────────────
 * SubmissionsPanel
 * Studio-side companion to the public "Send us your story" form.
 *
 *  • Renders a small Inbox pill in the Studio header with a live unread
 *    count, polled every 60 s.
 *  • Click to open a modal listing every submission (newest-first).
 *  • Each row offers `Use this →`, `Mark featured`, and `Archive` actions.
 *  • `Use this →` calls onUseSubmission(s) — the parent Studio then pre-fills
 *    title / script / source video URL / donation link / category in one
 *    motion. The submission's status flips to `reviewing` server-side.
 *
 * Auth: stored admin token in localStorage. Prompts once, re-prompts on 403.
 * ────────────────────────────────────────────────────────────────────────── */
export default function SubmissionsPanel({ onUseSubmission }) {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ new_count: 0, total: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const tokenRef = useRef(null);

  /** Load token from localStorage on first render. */
  useEffect(() => {
    tokenRef.current = (typeof window !== "undefined" && window.localStorage.getItem(TOKEN_KEY)) || "";
  }, []);

  /** Prompt once for the admin token; remember it for the session. */
  const ensureToken = () => {
    if (tokenRef.current) return tokenRef.current;
    const t = window.prompt("Admin token (Studio review access):", "storylo-admin");
    if (!t) return "";
    tokenRef.current = t;
    try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* localStorage might be blocked */ }
    return t;
  };

  const clearToken = () => {
    tokenRef.current = "";
    try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignored */ }
  };

  /** Lightweight count poll — runs every 60 s + on every focus. */
  const pollCount = async () => {
    const t = tokenRef.current;
    if (!t) return; // silently skip until admin first opens the panel
    try {
      const { data } = await axios.get(`${API}/admin/submissions/count`, {
        headers: { "X-Admin-Token": t },
        timeout: 8000,
      });
      setCounts(data);
    } catch (e) {
      if (e?.response?.status === 403) clearToken();
    }
  };

  useEffect(() => {
    pollCount();
    const id = setInterval(pollCount, 60_000);
    const onFocus = () => pollCount();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Full list fetch — runs every time the modal opens. */
  const loadItems = async () => {
    const t = ensureToken();
    if (!t) return;
    setLoading(true); setErr("");
    try {
      const { data } = await axios.get(`${API}/admin/submissions?limit=200`, {
        headers: { "X-Admin-Token": t },
        timeout: 15000,
      });
      setItems(data.items || []);
      setCounts({ new_count: data.new_count, total: data.total });
    } catch (e) {
      if (e?.response?.status === 403) {
        clearToken();
        setErr("Admin token rejected. Click the pill again to re-enter.");
      } else {
        setErr(e?.response?.data?.detail || e.message || "Couldn't load submissions.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onOpen = () => {
    setOpen(true);
    loadItems();
  };
  const onClose = () => setOpen(false);

  const setStatus = async (id, status) => {
    const t = ensureToken();
    if (!t) return;
    try {
      await axios.patch(`${API}/admin/submissions/${id}`,
        { status },
        { headers: { "X-Admin-Token": t }, timeout: 10000 },
      );
      // optimistic update
      setItems((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
      pollCount();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Update failed.");
    }
  };

  const onUseClick = async (s) => {
    if (typeof onUseSubmission === "function") onUseSubmission(s);
    if (s.status === "new") setStatus(s.id, "reviewing");
    setOpen(false);
  };

  const newCount = counts.new_count || 0;

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        title="Story submissions"
        aria-label="Story submissions"
        className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
        data-testid="submissions-pill"
      >
        <Inbox className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Submissions</span>
        {newCount > 0 ? (
          <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[10px] font-semibold leading-none"
                data-testid="submissions-pill-count">
            {newCount > 99 ? "99+" : newCount}
          </span>
        ) : counts.total > 0 ? (
          <span className="ml-1 text-[10px] text-[color:var(--muted)]">{counts.total}</span>
        ) : null}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto p-4 sm:p-8"
          onClick={onClose}
          data-testid="submissions-modal"
        >
          <div
            className="panel w-full max-w-3xl mx-auto my-4 sm:my-8 p-6 md:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 text-[color:var(--muted)] hover:text-white transition-colors p-1.5"
              data-testid="submissions-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3 mb-1">
              <Inbox className="w-5 h-5 text-[color:var(--accent)]" />
              <h2 className="font-display text-2xl">Story submissions</h2>
            </div>
            <p className="text-xs text-[color:var(--muted)] mb-6">
              {counts.total} total · <span className="text-amber-300">{counts.new_count || 0} new</span>. Click "Use this →" to pre-fill the Studio with the submission's title, message, source link &amp; category.
            </p>

            {err && <p className="text-xs text-rose-400 mb-3" data-testid="submissions-error">{err}</p>}

            {loading ? (
              <div className="flex items-center justify-center py-12 text-[color:var(--muted)] text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12 text-[color:var(--muted)] text-sm" data-testid="submissions-empty">
                Nothing here yet — submissions from the public "Share your story" form will land here.
              </div>
            ) : (
              <div className="space-y-3" data-testid="submissions-list">
                {items.map((s) => {
                  const sty = STATUS_STYLE[s.status] || STATUS_STYLE.new;
                  return (
                    <div
                      key={s.id}
                      className="rounded-xl border border-white/5 p-4 bg-white/[0.02]"
                      data-testid={`submission-${s.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full border ${sty.cls}`}>
                              {sty.label}
                            </span>
                            {s.category && (
                              <span className="text-[10px] tracking-[0.14em] uppercase text-[color:var(--muted)] inline-flex items-center gap-1">
                                <Tag className="w-3 h-3" /> {s.category.replace("_", " ")}
                              </span>
                            )}
                            <span className="text-[10px] text-[color:var(--muted)]">{fmtDate(s.created_at)}</span>
                          </div>
                          <h3 className="font-display text-lg leading-tight truncate" data-testid={`submission-${s.id}-title`}>
                            {s.title}
                          </h3>
                          <div className="text-xs text-[color:var(--muted)] mt-1 flex items-center gap-2 flex-wrap">
                            <span>{s.name}</span>
                            <span className="opacity-50">·</span>
                            <a href={`mailto:${s.email}`}
                               className="inline-flex items-center gap-1 hover:text-white transition-colors">
                              <Mail className="w-3 h-3" /> {s.email}
                            </a>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto pr-2 mb-3">
                        {s.message}
                      </p>

                      {s.source_url && (
                        <a
                          href={s.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[color:var(--accent)] hover:underline mb-3 break-all"
                          data-testid={`submission-${s.id}-link`}
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" /> {s.source_url}
                        </a>
                      )}

                      <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
                        <button
                          onClick={() => onUseClick(s)}
                          className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                          data-testid={`submission-${s.id}-use`}
                        >
                          <ArrowRight className="w-3 h-3" /> Use this
                        </button>
                        {s.status !== "featured" && (
                          <button
                            onClick={() => setStatus(s.id, "featured")}
                            className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                            data-testid={`submission-${s.id}-feature`}
                          >
                            <Star className="w-3 h-3" /> Mark featured
                          </button>
                        )}
                        {s.status !== "archived" && (
                          <button
                            onClick={() => setStatus(s.id, "archived")}
                            className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                            data-testid={`submission-${s.id}-archive`}
                          >
                            <Archive className="w-3 h-3" /> Archive
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
